/** Минимум, который нужен от денежной суммы GraphQL-схемы. */
export interface MoneyLike {
  value: number;
  currency: string;
  valueUSD?: number | null;
}

/**
 * Сумма как «значение валюта». Отсутствующее значение рендерится пустой
 * строкой, а не прочерком: общее правило проекта (см. formatUsd).
 */
export function formatMoney(amount?: MoneyLike | null): string {
  if (!amount) return "";
  const formatted = amount.value.toLocaleString("ru-RU", {
    maximumFractionDigits: 2,
  });
  return `${formatted} ${amount.currency}`;
}

/**
 * Две валюты в одной ячейке: сумма контракта и она же в валюте
 * организации. Вторая в скобках, и её нет, пока курс не зафиксирован.
 */
export function formatMoneyPair(
  primary?: MoneyLike | null,
  secondary?: MoneyLike | null
): string {
  const first = formatMoney(primary);
  const second = formatMoney(secondary);
  if (!second) return first;
  return `${first} (${second})`;
}

/** Дата без времени: биллинг живёт днями UTC. */
export function formatDay(value?: string | Date | null): string {
  if (!value) return "";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

/** Срок действия контракта: пустое окончание значит бессрочный контракт. */
export function formatContractPeriod(
  dateFrom?: string | Date | null,
  dateTo?: string | Date | null
): string {
  const to = dateTo ? formatDay(dateTo) : "бессрочно";
  return `${formatDay(dateFrom)}, ${to}`;
}

/** Минимум, который нужен от контракта для суммы за биллинг-период. */
export interface ContractPeriodAmountInput {
  currency: string;
  items: { amount?: MoneyLike | null }[];
}

/**
 * Сумма за биллинг-период: лицензия плюс разовые работы, без ревшера (у
 * строки доли с продаж суммы нет, процент считается отдельно).
 */
export function contractPeriodAmount(
  contract: ContractPeriodAmountInput
): string {
  const billable = contract.items.filter((item) => item.amount);
  if (billable.length === 0) return "";
  const total = billable.reduce(
    (sum, item) => sum + (item.amount?.value ?? 0),
    0
  );
  return formatMoney({ value: total, currency: contract.currency });
}

/** Минимум, который нужен от снимка курса GraphQL-схемы. */
export interface RateLike {
  rate: number;
  date: string | Date;
  source: string;
}

/**
 * Сумма платежа со счётом в другой валюте: «200 USD → 184,68 EUR», ставка
 * в валюте контракта и к оплате в валюте счёта. Без суммы в валюте счёта
 * только сумма контракта.
 */
export function formatInvoiceConversion(
  amount?: MoneyLike | null,
  invoiceAmount?: MoneyLike | null
): string {
  const first = formatMoney(amount);
  if (!invoiceAmount) return first;
  return `${first} → ${formatMoney(invoiceAmount)}`;
}

/**
 * Подсказка с курсом счёта. Курс с четырьмя знаками, как на печати, дата
 * это дата строки курса; FALLBACK значит, что ЕЦБ в этот день курс не
 * публиковал и взят ближайший предыдущий.
 */
export function formatInvoiceRate(
  from: string,
  rate?: RateLike | null,
  to?: string | null
): string {
  if (!rate || !to) return "";
  const fallback =
    rate.source === "FALLBACK" ? ", ближайший опубликованный" : "";
  return `Курс ЕЦБ 1 ${from} = ${rate.rate.toFixed(4)} ${to} на ${formatDay(rate.date)}${fallback}`;
}

/** Валюта контракта и, если отличается, валюта счёта: «USD → EUR». */
export function formatContractCurrency(
  currency: string,
  invoiceCurrency?: string | null
): string {
  if (!invoiceCurrency || invoiceCurrency === currency) return currency;
  return `${currency} → ${invoiceCurrency}`;
}
